import GeniePage from "../../../components/special/genie-page/GeniePage";
import GenieText from "../../../components/core/genie-text/GenieText";
import Box from "@mui/material/Box";
import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Snackbar,
  Alert,
  TableSortLabel,
} from "@mui/material";
import React, { useState } from "react";
import GenieStickyHeader from "../../../components/core/genie-sticky-header/GenieStickyHeader";
import GenieEmpty from "../../../components/core/genie-empty/GenieEmpty";
import GenieTableLoad from "../../../components/core/genie-table-load/GenieTableLoad";
import useCustomerOrders from "../../../queries/routes/useCustomerOrders";
import { useParams } from "react-router-dom";
import SendReorderEmailDialog from "./SendReorderEmailDialog";
import { fuzzyTimeAgo } from "../../../utils/date";
import dayjs from "dayjs";

type SortField = 'name' | 'last_order' | 'count';
type SortDirection = 'asc' | 'desc';

function Customers() {
  const { org_id } = useParams();
  const { data: customers, isLoading, refetch } = useCustomerOrders(org_id!);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const handleSendReorderEmail = (customer: any) => {
    setSelectedCustomer(customer);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setSelectedCustomer(null);
  };

  const handleEmailSuccess = () => {
    setSnackbar({
      open: true,
      message: "Reorder email sent successfully!",
      severity: "success",
    });
    setEmailDialogOpen(false);
    setSelectedCustomer(null);
    // Refresh the page data to update the sent time
    refetch();
  };

  const handleEmailError = (error: any) => {
    setSnackbar({
      open: true,
      message: error.message || "Failed to send reorder email",
      severity: "error",
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const sortCustomers = (customers: any[]) => {
    return [...customers].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'name':
          aValue = a.organization.name.toLowerCase();
          bValue = b.organization.name.toLowerCase();
          break;
        case 'last_order':
          aValue = a.last_order ? dayjs(a.last_order).valueOf() : 0;
          bValue = b.last_order ? dayjs(b.last_order).valueOf() : 0;
          break;
        case 'count':
          aValue = a.count;
          bValue = b.count;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const isLastOrderOver120Days = (lastOrderDate: string, lastReorderEmail?: string) => {
    if (!lastOrderDate && !lastReorderEmail) return false;

    const now = dayjs();
    let lastActivity = dayjs(lastOrderDate || '1970-01-01');

    if (lastReorderEmail) {
      const lastEmail = dayjs(lastReorderEmail);
      lastActivity = lastEmail.isAfter(lastActivity) ? lastEmail : lastActivity;
    }

    return now.diff(lastActivity, 'day') > 120;
  };

  return (
    <GeniePage noMargin>
      <SendReorderEmailDialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        customer={selectedCustomer}
        onSuccess={handleEmailSuccess}
        onError={handleEmailError}
      />
      <GenieStickyHeader>
        <Stack flexDirection={"row"} width={"100%"} alignItems={"center"}>
          <GenieText fontSize={"medium"} fontWeight={"bold"}>
            Customers
          </GenieText>
        </Stack>
      </GenieStickyHeader>
      <Box mt={4}>
        {isLoading && (
          <GenieTableLoad
            rowCount={10}
            columnCount={5}
            sx={{ minWidth: 650 }}
          />
        )}
        {!isLoading && customers?.length === 0 && (
          <GenieEmpty>Customers haven't been added yet</GenieEmpty>
        )}
      </Box>
      {!isLoading && customers && customers.length > 0 && (
        <TableContainer component={Box}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left">
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Customer Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left">
                  <TableSortLabel
                    active={sortBy === 'last_order'}
                    direction={sortBy === 'last_order' ? sortDirection : 'asc'}
                    onClick={() => handleSort('last_order')}
                  >
                    Last Order
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortBy === 'count'}
                    direction={sortBy === 'count' ? sortDirection : 'asc'}
                    onClick={() => handleSort('count')}
                  >
                    Order Count
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">History</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortCustomers(customers).map((customer) => (
                <TableRow key={customer.organization.id}>
                  <TableCell align="left">
                    <Stack direction={"row"} alignItems={"center"}>
                      {customer.organization.name}
                    </Stack>
                  </TableCell>
                  <TableCell align="left">
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <span>{customer.last_order || "N/A"}</span>
                      {customer.last_order && isLastOrderOver120Days(customer.last_order, customer.last_reorder_email) && (
                        <Chip
                          label={fuzzyTimeAgo(customer.last_order)}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem" }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">{customer.count}</TableCell>
                  <TableCell align="right">
                    <GenieText
                      fontSize={"tiny"}
                      link={`${customer.organization.id}`}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      View Orders
                    </GenieText>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="column" spacing={1} alignItems="flex-end">
                      <Button
                        variant={isLastOrderOver120Days(customer.last_order, customer.last_reorder_email) ? "contained" : "outlined"}
                        size="small"
                        onClick={() => handleSendReorderEmail(customer)}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          fontFamily: "IBM Plex Sans, 'roboto', sans-serif",
                          px: 1.5,
                          width: "fit-content", // Don't span full width
                          minWidth: "auto", // Remove minimum width constraint
                          ...(isLastOrderOver120Days(customer.last_order, customer.last_reorder_email)
                            ? {
                                backgroundColor: "#A19AED",
                                color: "white",
                                boxShadow: "none",
                                "&:hover": {
                                  backgroundColor: "#8B7FE8",
                                  boxShadow: "none",
                                },
                              }
                            : {
                                color: "#666",
                                borderColor: "#ccc",
                                "&:hover": {
                                  borderColor: "#999",
                                  color: "#333",
                                },
                              }
                          ),
                        }}
                      >
                        Send Reorder Email
                      </Button>
                      {customer.last_reorder_email && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem", textAlign: "center" }}
                        >
                          Reorder Email Sent on {dayjs(customer.last_reorder_email).format("YYYY-MM-DD")}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </GeniePage>
  );
}

export default Customers;
